package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.billing.OwnerPlan
import com.evoapps.evoshift.domain.usecase.billing.ManageSubscriptionUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObserveOwnerPlanUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObserveSeatsInUseUseCase
import com.evoapps.evoshift.domain.usecase.billing.RestorePurchasesUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.paywall_restore_nothing
import evoshift.composeapp.generated.resources.subscription_restore_done
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class SubscriptionUiState(
    /** Null until the plan is known (the spinner shows only then). */
    val plan: OwnerPlan? = null,
    /** People in the owner's team across all workplaces; null until known. */
    val seatsInUse: Int? = null,
    val isManaging: Boolean = false,
    val isRestoring: Boolean = false,
    val message: UiText? = null,
    val error: UiText? = null
)

/**
 * The owner's plan and subscription (SH-38, SH-52), as EVoLabels' SubscriptionStatusViewModel:
 * the trial or the plan with its seats in use, renewal and store, and the ways to manage or
 * restore it.
 */
class SubscriptionViewModel(
    private val observeOwnerPlan: ObserveOwnerPlanUseCase,
    private val manageSubscription: ManageSubscriptionUseCase,
    private val restorePurchases: RestorePurchasesUseCase,
    private val observeSeatsInUse: ObserveSeatsInUseUseCase? = null
) : ViewModel() {
    private val _state = MutableStateFlow(SubscriptionUiState())
    val state: StateFlow<SubscriptionUiState> = _state.asStateFlow()

    init {
        viewModelScope.launch {
            // SH-188: a failed listener leaves the screen with a message, not an ended coroutine.
            observeOwnerPlan().catch { e -> _state.update { it.copy(error = e.toUiText()) } }.collect { plan -> _state.update { it.copy(plan = plan) } }
        }
        observeSeatsInUse?.let { seats ->
            viewModelScope.launch {
                seats().catch { e -> _state.update { it.copy(error = e.toUiText()) } }.collect { used -> _state.update { it.copy(seatsInUse = used) } }
            }
        }
    }

    fun manage() {
        val plan = _state.value.plan ?: return
        if (_state.value.isManaging) return
        _state.update { it.copy(isManaging = true, error = null) }
        viewModelScope.launch {
            val error = manageSubscription(plan).exceptionOrNull()
            _state.update { it.copy(isManaging = false, error = error?.toUiText()) }
        }
    }

    fun restore() {
        if (_state.value.isRestoring) return
        _state.update { it.copy(isRestoring = true, error = null, message = null) }
        viewModelScope.launch {
            restorePurchases().fold(
                onSuccess = { restored ->
                    val found = restored || _state.value.plan?.isPaid == true
                    _state.update {
                        it.copy(
                            isRestoring = false,
                            message = UiText.Resource(if (found) Res.string.subscription_restore_done else Res.string.paywall_restore_nothing)
                        )
                    }
                },
                onFailure = { e -> _state.update { it.copy(isRestoring = false, error = e.toUiText()) } }
            )
        }
    }

    fun messageShown() = _state.update { it.copy(message = null, error = null) }
}
